/**
 * The "Three Rivers" rating mark (LOCKED = water waves). The rating count is shown as that many
 * STACKED wave lines so it reads at a glance: 1 River = one line, 2 = two, 3 = three. Each line is
 * a doubled glyph — a back wave (#8FD0FF) offset beneath a front wave (#3AA6FF). `count === 0`
 * ("Wasn't for me") renders a single flat muted line — no rivers.
 *
 * The waves loop with a subtle horizontal flow (the path is drawn wider than the viewport and the
 * group translates by one period). Honors prefers-reduced-motion via the global theme rule.
 * `inline` renders a small version used in headings.
 * @see CLAUDE.md (Rating mark)  @see docs/05-CLAUDE-CODE-BUILD-BRIEF.md §3
 */
import type { CSSProperties } from 'react';

interface WaveGlyphProps {
  count: 0 | 1 | 2 | 3;
  /** Small variant for inline use in a heading. */
  inline?: boolean;
}

export function WaveGlyph({ count, inline = false }: WaveGlyphProps) {
  const SEG = inline ? 8 : 16; // half-period width
  const AMP = inline ? 3 : 5; // amplitude
  const GAP = inline ? 6 : 14; // spacing between stacked lines
  const OFFSET = inline ? 1.5 : 2.5; // back-wave offset
  const LINE_W = inline ? 30 : 96; // visible width
  const STROKE = inline ? 2 : 3;
  const PAD = inline ? 3 : 5;
  const EXTRA = 2; // extra segments past the viewport for a seamless flow loop
  const period = SEG * 2;

  const maxHeight = (3 - 1) * GAP + AMP + OFFSET + PAD;

  function waveLine(y: number): string {
    const segments = Math.round(LINE_W / SEG) + EXTRA;
    let d = `M0 ${y} q ${SEG / 2} -${AMP} ${SEG} 0`;
    for (let i = 1; i < segments; i += 1) d += ` t ${SEG} 0`;
    return d;
  }

  if (count === 0) {
    const y = maxHeight / 2;
    return (
      <svg width={LINE_W} height={maxHeight} viewBox={`0 0 ${LINE_W} ${maxHeight}`} aria-hidden="true" className="wave-glyph">
        <line x1={4} y1={y} x2={LINE_W - 4} y2={y} stroke="var(--wave-muted)" strokeWidth={STROKE + 0.5} strokeLinecap="round" />
      </svg>
    );
  }

  const blockHeight = (count - 1) * GAP + AMP + OFFSET + PAD;
  const top = (maxHeight - blockHeight) / 2 + AMP + 1; // vertically center the stack
  const lines = Array.from({ length: count }, (_, i) => top + i * GAP);

  return (
    <svg width={LINE_W} height={maxHeight} viewBox={`0 0 ${LINE_W} ${maxHeight}`} aria-hidden="true" className="wave-glyph">
      {lines.map((y, idx) => {
        const d = waveLine(y);
        const style = { '--wave-period': `${period}px`, animationDelay: `${idx * -0.5}s` } as CSSProperties;
        return (
          <g key={y} className="wave-flow" style={style}>
            {/* back wave, offset down */}
            <path d={d} transform={`translate(0,${OFFSET})`} stroke="var(--wave-back)" strokeWidth={STROKE} fill="none" strokeLinecap="round" />
            {/* front wave */}
            <path d={d} stroke="var(--wave-front)" strokeWidth={STROKE} fill="none" strokeLinecap="round" />
          </g>
        );
      })}
    </svg>
  );
}
